import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, Reply, Trash2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { EventReview } from "@/components/EventReviews";

export const ReplyView = ({ r }: { r: EventReview }) => {
  const { t } = usePrefs();
  if (!r.reply) return null;
  return (
    <div data-testid={`review-reply-${r.review_id}`} className="mt-2 ms-4 border-s-2 border-primary/50 ps-3 text-sm">
      <p className="text-xs font-bold text-primary">{t("rp.from").replace("{name}", r.reply.by)}</p>
      <p dir="auto" className="whitespace-pre-line">{r.reply.text}</p>
    </div>
  );
};

export const ReplyEditor = ({ r, onSaved }: { r: EventReview; onSaved: (x: EventReview) => void }) => {
  const { t } = usePrefs();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(r.reply?.text || "");
  const save = async () => {
    try { const { data } = await api.put(`/venue/event-reviews/${r.review_id}/reply`, { text }); onSaved(data); setOpen(false); toast.success(t("rp.saved")); } catch (e) { toast.error(errorText(e, t)); }
  };
  const del = async () => {
    if (!window.confirm(t("rp.confirmDelete"))) return;
    try { await api.delete(`/venue/event-reviews/${r.review_id}/reply`); onSaved({ ...r, reply: null }); setText(""); toast.success(t("rp.deleted")); } catch (e) { toast.error(errorText(e, t)); }
  };
  if (!open) return (
    <div className="flex gap-2 pt-1">
      <button onClick={() => setOpen(true)} data-testid={`review-reply-open-${r.review_id}`} className="h-8 px-3 rounded-full border bg-background text-xs font-bold inline-flex items-center gap-1.5">
        {r.reply ? <Pencil className="h-3.5 w-3.5" /> : <Reply className="h-3.5 w-3.5" />}{t(r.reply ? "rp.edit" : "rp.reply")}
      </button>
      {r.reply && <button onClick={del} data-testid={`review-reply-delete-${r.review_id}`} aria-label={t("rp.delete")} className="h-8 w-8 grid place-items-center rounded-full border bg-background text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
    </div>
  );
  return (
    <div className="space-y-2 pt-1">
      <textarea dir="auto" value={text} maxLength={500} rows={3} onChange={(e) => setText(e.target.value)} placeholder={t("rp.placeholder")} data-testid={`review-reply-input-${r.review_id}`} className="w-full rounded-2xl border bg-background p-3 text-sm" />
      <div className="flex gap-2">
        <button onClick={save} disabled={!text.trim()} data-testid={`review-reply-save-${r.review_id}`} className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold disabled:opacity-50">{t("rp.publish")}</button>
        <button onClick={() => setOpen(false)} className="h-9 px-4 rounded-full border text-xs font-bold">{t("common.cancel")}</button>
      </div>
    </div>
  );
};
